import React from 'react';

import { Button, RadioGroup, Text } from '@ui-library';

import type { ProjectStacksState } from '../../hooks/useProjectStacks';
import type { CustomStackPicker, StackPreset } from '../../services/projectStacks';
import { CUSTOM_STACK, StackChoice, chooseStack, stackSummary } from '../../utils/stackPicker';
import { CustomStackFields } from './CustomStackFields';
import { InstructionsField } from './InstructionsField';
import { StackInfo } from './StackInfo';

/** The line under the Special Instructions box while its text is within the cap. */
const INSTRUCTIONS_HINT =
  'Optional. Describe what the AI Model should follow, such as folder structure, naming, coding standards or extra libraries.';

/** An option the list shows before the presets: the Clone drawer's "Same as the source project". */
interface LeadingStackOption {
  value: string;
  label: string;
  /** The line under its label, such as the source's stack label. */
  description?: string;
}

interface TechStackFieldProps<T extends StackChoice> {
  /** The list's radio group name, which starts its options' ids. */
  name: string;
  /** The line under the heading, such as Create Project's "Chosen once, when the project is created.". */
  hint?: string;
  /** The tech stacks GET /projects/stacks offers, as the dashboard last read them. */
  stacks: ProjectStacksState;
  /** Reads them again: Try again, when they couldn't be read. */
  reloadStacks: () => void;
  /** The drawer's input, which holds the choice. */
  choice: T;
  onChange: (choice: T) => void;
  leadingOption?: LeadingStackOption;
  /** The presets to list: the payload's when left out. */
  presets?: StackPreset[];
  /** Under the list, and under the Custom option's groups while they are open,
   *  such as the Clone drawer's line about the source code. */
  belowList?: React.ReactNode;
  /** Whether the Special Instructions box shows while the Custom option is offered:
   *  the Clone drawer hides it for "Same as the source project". */
  showBox?: boolean;
  /** The box's id. */
  boxId: string;
}

/**
 * The Tech Stack section of the Create Project and Clone drawers (multi-stack
 * steps 3.14g and 3.14h, layout A of the owner's pick, 2026-10-20): the presets
 * with their (i), then, while the server offers it, the Custom option, whose
 * groups open right under its row (CustomStackFields), and one Special
 * Instructions box under the list for any choice; until the list is read,
 * "Loading tech stacks…", or why it couldn't be read with Try again. The box
 * shows only while the Custom option is offered: the create API refuses special
 * instructions until then. The drawer holds the choice, sends it
 * (createStackFields) and names the stack in its footer (StackNameInFooter).
 */
export function TechStackField<T extends StackChoice>({
  name,
  hint,
  stacks,
  reloadStacks,
  choice,
  onChange,
  leadingOption,
  presets,
  belowList,
  showBox = true,
  boxId,
}: TechStackFieldProps<T>) {
  const listed = presets ?? (stacks.kind === 'loaded' ? stacks.presets : []);
  const custom = stacks.kind === 'loaded' ? stacks.custom : null;
  const customChosen = !!custom && choice.stack === CUSTOM_STACK;

  return (
    <>
      <Text variant="text-md" weight="semibold" color="primary" className={hint ? undefined : 'mb-bg-v2-lg'}>Tech Stack</Text>
      {hint && <Text variant="text-sm" color="tertiary" className="mb-bg-v2-lg">{hint}</Text>}
      {stacks.kind === 'loaded' ? (
        <RadioGroup
          name={name}
          size="sm"
          value={choice.stack ?? ''}
          onChange={(stack) => onChange(chooseStack(choice, stack))}
          options={[
            ...(leadingOption ? [{
              value: leadingOption.value,
              label: <Text variant="text-sm" weight="semibold" color="tertiary" as="span">{leadingOption.label}</Text>,
              description: leadingOption.description,
            }] : []),
            ...listed.map((preset) => ({
              value: preset.id,
              label: <Text variant="text-sm" weight="semibold" color="tertiary" as="span">{preset.label}</Text>,
              labelAddon: preset.info.length > 0 ? <StackInfo label={preset.label} lines={preset.info} /> : undefined,
            })),
            // The Custom option, only while the server offers it.
            ...(custom ? [{
              value: CUSTOM_STACK,
              label: <Text variant="text-sm" weight="semibold" color="tertiary" as="span">Custom</Text>,
              description: 'Pick your own frontend, backend and database, or describe your stack.',
            }] : []),
          ]}
        />
      ) : stacks.kind === 'error' ? (
        <div className="flex items-center gap-bg-v2-md">
          <Text variant="text-sm" color="error" as="span">Tech stacks couldn't be loaded.</Text>
          <Button type="button" variant="link" size="small" label="Try again" onClick={reloadStacks} />
        </div>
      ) : (
        <Text variant="text-sm" color="tertiary">Loading tech stacks…</Text>
      )}
      {/* Custom's groups, right under its row and in line with its text. */}
      {customChosen && custom && (
        <div className="mt-bg-v2-lg pl-bg-v2-3xl">
          <CustomStackFields
            picker={custom}
            picks={choice.stackPicks ?? {}}
            onChange={(stackPicks) => onChange({ ...choice, stackPicks })}
          />
        </div>
      )}
      {belowList}
      {/* One box for any choice, while the Custom option is offered: the create
          API refuses special instructions until then. */}
      {custom && showBox && (
        <div className="mt-bg-v2-3xl">
          <InstructionsField
            id={boxId}
            value={choice.customInstructions ?? ''}
            onChange={(customInstructions) => onChange({ ...choice, customInstructions })}
            maxChars={custom.instructions_max_chars}
            hint={INSTRUCTIONS_HINT}
          />
        </div>
      )}
    </>
  );
}

interface StackNameInFooterProps {
  /** The Custom option's picker. */
  custom: CustomStackPicker;
  choice: StackChoice;
}

/** The stack's name at the left of a drawer's footer, beside Cancel, while the
 *  Custom option is chosen (stackSummary), in view however far the form scrolls;
 *  the buttons keep their size and the name wraps on a narrow screen. Empty for
 *  any other choice, which its row names. */
export const StackNameInFooter: React.FC<StackNameInFooterProps> = ({ custom, choice }) => {
  const summary = stackSummary(custom, choice);
  return (
    <div className="mr-auto min-w-0" aria-live="polite">
      {summary && (
        <Text
          variant="text-sm"
          weight={summary.quiet ? 'regular' : 'semibold'}
          color={summary.quiet ? 'tertiary' : 'primary'}
        >
          {summary.text}
        </Text>
      )}
    </div>
  );
};
